import React, { useEffect, useState } from "react";
import { Box, Typography, Avatar, useTheme } from "@mui/material";
import CallEndIcon from "@mui/icons-material/CallEnd";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import CloseIcon from "@mui/icons-material/Close";
import BetaTag from "./BetaTag";
import TransferMenu from "./TransferMenu";
import { Call as CallIconsax } from "iconsax-react";
import { nameToContactAvatarImage } from "../../../config/AvatarColor";
import PlatformIcon from "../../../components/Inbox/Features/PlatformIcon";
import { actionBtnSx, actionIconBoxSx } from "../../../utils/callActionButtonStyles";
import IconButtonWithTooltip from "../../../components/Inbox/Shared/IconButtonWithTooltip";

function formatElapsed(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

/**
 * Single floating card, top-right, for the whole life of a call — ringing
 * through connected. Replaces the old centered Dialog (IncomingCallBanner)
 * and full-width top bar (ActiveCallBar): no backdrop, no page-blocking, the
 * rest of the app stays fully usable underneath and around it. Mounted once
 * by CallManager; incoming and active are mutually exclusive by the time
 * they reach here (see CallManager's render guard).
 */
export default function CallWidget({ incoming, active, onAnswer, onReject, onHangup, onInitiateTransfer, onCancelTransfer }) {
  const theme = useTheme();
  const [elapsed, setElapsed] = useState(0);
  const [transferAnchorEl, setTransferAnchorEl] = useState(null);
  const isRinging = Boolean(incoming) && !active;
  const call = active || incoming;

  // Only depends on the primitive connectedAt (not the active object itself)
  // so this doesn't reset the interval on every unrelated active-call field
  // update (e.g. a metaStatus ping) — just when a call actually connects.
  const connectedAt = active?.status === "connected" ? active.connectedAt : null;
  useEffect(() => {
    if (!connectedAt) return undefined;
    const tick = () => setElapsed(Math.floor((Date.now() - connectedAt) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [connectedAt]);

  if (!call) return null;

  const displayName = call.contactName || call.remoteNumber || call.from;
  const showNumberLine = Boolean(
    (call.contactName || call.remoteNumber) && call.from && (call.contactName || call.remoteNumber) !== call.from
  );
  const initials = (displayName || "?").slice(0, 2).toUpperCase();

  const isConnected = active?.status === "connected";
  const transferPending = active?.transferPending;
  const canTransfer = isConnected && active?.bridgeMode === "sfu" && !transferPending;
  const statusLabel = isRinging
    ? "Incoming call"
    : transferPending
    ? `Transferring to ${transferPending.toAgentName}…`
    : isConnected
    ? formatElapsed(elapsed)
    : `Calling ${active?.direction === "outbound" ? "out" : ""}…`;

  return (
    <Box
      role="dialog"
      aria-label={isRinging ? "Incoming call" : "Active call"}
      sx={{
        position: "fixed",
        // 64px = ChatboxNavbar's own real total height (see
        // inboxLayoutConstants.js's CHATBOX_NAVBAR_HEADER_TOTAL_PX) + the
        // original small margin — this widget is mounted globally (rings on
        // any page, not just Inbox), so rather than make it route-aware it
        // just always sits below where that navbar would be (Rahim,
        // 2026-08-20: "move down everywhere").
        top: { xs: 76, sm: 80 },
        right: { xs: 12, sm: 16 },
        left: { xs: 12, sm: "auto" },
        zIndex: theme.zIndex.snackbar + 1,
        width: { xs: "auto", sm: 336 },
        maxWidth: 400,
        borderRadius: 3,
        overflow: "hidden",
        bgcolor: theme.palette.mode === "dark" ? "#1c2320" : "#ffffff",
        boxShadow: "0 12px 32px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.12)",
        border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
        animation: "callWidgetSlideIn 260ms cubic-bezier(0.16, 1, 0.3, 1)",
        "@keyframes callWidgetSlideIn": {
          from: { opacity: 0, transform: "translateY(-12px) scale(0.98)" },
          to: { opacity: 1, transform: "translateY(0) scale(1)" },
        },
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.75 }}>
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          {isRinging && (
            <Box
              sx={{
                position: "absolute",
                inset: -4,
                borderRadius: "50%",
                border: "2px solid #1f7a6c",
                animation: "callWidgetPulse 1.6s ease-out infinite",
                "@keyframes callWidgetPulse": {
                  "0%": { transform: "scale(0.9)", opacity: 0.9 },
                  "100%": { transform: "scale(1.35)", opacity: 0 },
                },
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}
            />
          )}
          {/* Same avatar source Chats/Calls use (nameToContactAvatarImage),
              instead of a flat-color initials circle — falls back to the
              initials automatically when there's no image for this name. */}
          <Avatar src={nameToContactAvatarImage(displayName)} sx={{ width: 44, height: 44, bgcolor: "#1f7a6c", fontSize: 16, fontWeight: 600 }}>{initials}</Avatar>
          {/* Platform badge — calling is WhatsApp-only today (see
              CallModel.js's own comment), same PlatformIcon/ring convention
              as Chats/Calls. sx={{ml:0}} cancels PlatformIcon's own baked-in
              ml:0.5 default — without it the ring's width/height mismatch
              turns the border-radius:50% ring into an oval, so the icon
              reads as off-center (same bug already fixed in LiveCallCard.jsx,
              repeated here — Rahim caught it live, 2026-08-20). The separate
              "connected" green dot was dropped per the same feedback — this
              badge is enough on its own. */}
          {/* display:flex is load-bearing — a block-level shrink-to-fit box's
              inline-flex child gets sized via baseline/line-height rules
              that pull in the SURROUNDING font-size context, which differs
              here (a global, larger-font widget) from the Calls list's
              compact rows — same code rendered a circle there, an oval here.
              flex removes that font-size dependency entirely (Rahim caught
              this live, 2026-08-20 — this was the real fix, not another sx
              tweak). */}
          <Box sx={{ position: "absolute", bottom: -2, right: -2, borderRadius: "50%", border: `2px solid ${theme.palette.mode === "dark" ? "#1c2320" : "#ffffff"}`, display: "flex" }}>
            <PlatformIcon platform="whatsapp" size={16} sx={{ ml: 0 }} />
          </Box>
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: isRinging ? "#1f7a6c" : "text.secondary",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {statusLabel}
            </Typography>
            <BetaTag sx={{ height: 16, "& .MuiChip-label": { px: 0.75, fontSize: 9.5 } }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 15,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.3,
            }}
          >
            {displayName}
          </Typography>
          {showNumberLine && (
            <Typography sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {call.from}
            </Typography>
          )}
        </Box>

        {isRinging ? (
          // Same square Answer/Decline buttons as the Calls list
          // (callActionButtonStyles.js) — was a round, differently-styled
          // pair here, per Rahim's ask (2026-08-20) to match.
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <IconButtonWithTooltip label="Decline" onClick={onReject} size="small" sx={actionBtnSx(theme.palette.error.main, false)} aria-label="Decline">
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButtonWithTooltip>
            <IconButtonWithTooltip label="Answer" onClick={onAnswer} size="small" sx={actionBtnSx(theme.palette.success.main, true)} aria-label="Answer">
              <Box sx={actionIconBoxSx}><CallIconsax size="100%" color="currentColor" variant="Bold" /></Box>
            </IconButtonWithTooltip>
          </Box>
        ) : (
          // Same square button style as Answer/Decline (callActionButtonStyles.js)
          // — was round/old-style here, per Rahim's ask (2026-08-20) to match.
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            {transferPending && (
              <IconButtonWithTooltip label="Cancel transfer" onClick={() => onCancelTransfer?.(active.callId)} size="small" sx={actionBtnSx(theme.palette.text.secondary, false)} aria-label="Cancel transfer">
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButtonWithTooltip>
            )}
            {canTransfer && (
              <IconButtonWithTooltip label="Transfer" onClick={(e) => setTransferAnchorEl(e.currentTarget)} size="small" sx={actionBtnSx(theme.palette.text.secondary, false)} aria-label="Transfer call">
                <PhoneForwardedIcon sx={{ fontSize: 18 }} />
              </IconButtonWithTooltip>
            )}
            <IconButtonWithTooltip label="Hang up" onClick={onHangup} size="small" sx={actionBtnSx(theme.palette.error.main, true)} aria-label="Hang up">
              <CallEndIcon sx={{ fontSize: 18 }} />
            </IconButtonWithTooltip>
          </Box>
        )}
      </Box>

      <TransferMenu
        anchorEl={transferAnchorEl}
        onClose={() => setTransferAnchorEl(null)}
        onSelect={(agent) => onInitiateTransfer?.(active.callId, agent.agentId, agent.name)}
      />
    </Box>
  );
}
